const weekdayShort = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So']

import { Modal, useActiveDate } from '@goodlok/react-sdk'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { useRoleIds } from '../app/utils/useRoleIds'
import { AdminPageData, useAdminPageData } from '../utilities/useAdminPageData'
import styles from './AdminShiftTile.module.sass'
import { AdminShiftTileSegment } from './AdminShiftTileSegment'
import { DayDetail } from './DayDetail'

export type AdminShiftTileProps = {
	employeeRoles: NonNullable<AdminPageData['getVenue']>['employeeRoles']
	day: NonNullable<
		NonNullable<ReturnType<typeof useAdminPageData>['data']>['getVenue']
	>['timetableDays'][number]
	setChange: (changed: boolean) => void
	updateData: () => Promise<void>
	isDeadlineEnabled: boolean
	unassignReasons: NonNullable<AdminPageData['getVenue']>['enabledUnassignReasons']
}

export const AdminShiftTile: FunctionComponent<AdminShiftTileProps> = ({
	employeeRoles,
	day,
	setChange,
	updateData,
	isDeadlineEnabled,
	unassignReasons,
}) => {
	const { id: dayId, date, tipsCents, shiftGroups } = day

	const [roles] = useRoleIds()
	const positions =
		shiftGroups &&
		shiftGroups.filter((position) => roles?.some((role) => position.type?.id === role))
	const allLocked = useMemo(
		() =>
			shiftGroups?.every((shiftGroup) => shiftGroup.shifts.every((shift) => shift.locked === true)),
		[shiftGroups]
	)
	const [padlockLocked, setPadlockLocked] = useState(!allLocked)
	useEffect(() => {
		setPadlockLocked(!allLocked)
	}, [allLocked])

	const [isTipsModalOpen, setIsTipsModalOpen] = useState(false)

	const { activeDate, setActiveDate } = useActiveDate()
	const activeMonth = useMemo(() => activeDate.month, [activeDate])

	const { dateFormatted, weekday, isCurrentDate } = useMemo(() => {
		const dateFormatted = new Date(date)
		const today = new Date()
		today.setUTCHours(0, 0, 0, 0)

		return {
			dateFormatted,
			weekday: dateFormatted.getDay(),
			isCurrentDate: today.getTime() === dateFormatted.getTime(),
		}
	}, [date])

	const tileRef = useRef<HTMLDivElement>(null)
	const { query } = useRouter()

	useEffect(() => {
		if (isCurrentDate && tileRef.current && query.date === undefined) {
			setTimeout(() => {
				if (tileRef.current) {
					window.scrollTo({ top: tileRef.current.offsetTop - 100, behavior: 'smooth' })
				}
			}, 500) // @TODO ugly solution to scroll only after the data is loaded
		}
	}, [isCurrentDate, query.date, tileRef])

	return (
		<div className={styles.wrapper} ref={tileRef}>
			<div
				className={clsx(
					styles.in,
					styles[`view_weekday_${weekday === 0 ? 7 : weekday}`],
					activeMonth !== new Date(date).getUTCMonth() && styles.is_disabled,
					positions && positions.length > 0 && isCurrentDate && styles.is_current
				)}>
				<div className={styles.weekdayDateWrapper}>
					<div className={styles.weekdayShort}>{weekdayShort[weekday]}</div>
					<div className={styles.dayDateAndLock}>
						<div className={styles.dayDate}>
							<button
								className={clsx(
									styles.dayDateButton,
									day.notes.length > 0 && styles.view_withNotes
								)}
								type="button"
								onClick={() => {
									setIsTipsModalOpen(true)
								}}>
								{dateFormatted.getDate()}
							</button>
						</div>
						{/* @TODO remove deadlines? */}
						{isDeadlineEnabled ? (
							<button
								className={styles.lockIcon}
								type="button"
								onClick={async () => {
									setPadlockLocked(!padlockLocked)

									await fetch(padlockLocked ? '/api/lock-day' : '/api/unlock-day', {
										method: 'POST',
										body: JSON.stringify({
											date: date,
										}),
									})
									await updateData()
								}}
								title={'potvrdit a zamknout celý den'}>
								<div
									className={clsx(
										styles.lockShackle,
										padlockLocked && styles.unlocked,
										!padlockLocked && styles.locked
									)}></div>
								<div className={styles.lockBody}></div>
							</button>
						) : null}
					</div>
				</div>
				<div className={styles.date}>
					<div className={styles.positions}>
						{positions &&
							positions.map((position) => {
								return (
									<AdminShiftTileSegment
										dayId={dayId}
										date={date}
										position={position}
										employeeRole={employeeRoles.find(
											(employeeRole) => employeeRole.role?.id === position.type?.id
										)}
										setChange={setChange}
										updateData={updateData}
										key={position.id}
										isOnlyPositionOfGivenRoleForTheDay={
											!positions.find(
												(positionInner) =>
													positionInner.id !== position.id &&
													positionInner.type?.id === position.type?.id
											)
										}
										unassignReasons={unassignReasons}
									/>
								)
							})}
					</div>
				</div>
			</div>

			<Modal
				isOpen={isTipsModalOpen}
				onRequestClose={() => {
					setIsTipsModalOpen(false)
				}}>
				<DayDetail
					date={date}
					tipsCents={tipsCents}
					dayId={dayId}
					positions={positions}
					notes={day.notes}
				/>
			</Modal>
			{activeMonth !== new Date(date).getUTCMonth() && (
				<button
					className={styles.monthSwitch}
					type="button"
					onClick={() => {
						if (new Date(date).getUTCMonth() < activeMonth) {
							const newMonth = (12 + activeDate.month - 1) % 12
							const newYear = activeDate.month - 1 < 0 ? activeDate.year - 1 : activeDate.year
							setActiveDate({ year: newYear, month: newMonth })
						} else {
							const newMonth = (activeDate.month + 1) % 12
							const newYear = activeDate.month + 1 >= 12 ? activeDate.year + 1 : activeDate.year
							setActiveDate({ year: newYear, month: newMonth })
						}
					}}
				/>
			)}
		</div>
	)
}
